// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import { useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";
import { TOAST_TYPE } from "@swiggy-private/payment-core";
import {
    ApiContext,
    CheckoutActionTypes,
    CheckoutContext,
    NEWRELIC_TABLES,
    getBridgeSdk,
    recordNREvent,
} from "@swiggy-private/checkout-core";

// contexts
import { InAppUpiContextValue } from "@payment-client/contexts/InAppUpiContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// constants
import { INAPP_UPI_ERROR_TITLE, INAPP_UPI_STATE, INAPP_UPI_SUCCESS_TITLE } from "@payment-client/constants/InAppUpi";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";

// helpers
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";
import { getInAppUpiGroup, processInAppUpiBridgeRequest } from "@payment-client/helpers/InAppUpiHelper";
import { isEmpty } from "@swiggy-private/js-utils";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { JuspayInAppUpiResponsePayload } from "@swiggy-private/native-bridge";
import { getPublicJuspayClientAuthToken } from "@payment-client/apis/publicJuspay";
export const useInAppUpi = (
    paymentResponse: GetPaymentMethodsResponse,
    isSdkAvailable: boolean,
): InAppUpiContextValue => {
    const isMountedRef = useMountedRef();
    const { reloadPayments } = useContext(ShowPaymentContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const apiContextValue = useContext(ApiContext);
    const apiHost = apiContextValue.host.unwrap();
    const tenant = useMemo(() => paymentResponse.tenant, [paymentResponse]);
    const group = paymentResponse.paymentGroup?.find(
        (group) => group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.INAPP_UPI,
    );
    let merchantId = group?.meta?.merchantId || "";
    if (isEmpty(merchantId)) {
        const groupUpi = paymentResponse.paymentGroup
            ?.find((group) => group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.UPI)
            ?.paymentMethods.find((method) => method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.INAPP_UPI);
        merchantId = groupUpi?.meta.merchantId || "";
    }

    const isDiscrepancyExists = useCallback(async () => {
        if (paymentResponse == null) {
            return { discrepancy: false };
        }
        const inAppUpiGroup = getInAppUpiGroup(paymentResponse.paymentGroup);
        if (inAppUpiGroup.isNone()) {
            return { discrepancy: false };
        }
        const inAppUpiHash = inAppUpiGroup.unwrap()?.meta?.inappUpiAccountsHash;
        if (inAppUpiHash != null) {
            const sdkData = await getBridgeSdk().getInAppUpiHash({
                ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
            });
            const sdkInAppUpiHash = sdkData?.inAppUPIAccountsHash;
            if (sdkInAppUpiHash !== inAppUpiHash) {
                return { discrepancy: true, inAppUpiHash, sdkInAppUpiHash };
            }
        }
        return { discrepancy: false };
    }, [paymentResponse, tenant]);

    useEffectOnce(() => {
        const isDiscrepancyExistsFn = async () => {
            const result = await isDiscrepancyExists();
            if (result.discrepancy) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.SWIGGY_UPI_ACCOUNTS_MISMATCH,
                    paymentInAppUpiHash: result?.inAppUpiHash || "",
                    sdkInAppUpiHash: result?.sdkInAppUpiHash || "",
                });
            }
        };

        // if inapp upi accounts hash is different from sdk and backend, we will log it to new relic to track the discrepancy
        isDiscrepancyExistsFn();
    });

    const shouldShowError = useMemo(() => {
        return !isSdkAvailable || merchantId == null || tenant == null || isMountedRef.current == null;
    }, [isMountedRef, isSdkAvailable, merchantId, tenant]);

    const makeInAppUpiRequest = useCallback(
        async (
            requestState: INAPP_UPI_STATE,
        ): Promise<JuspayInAppUpiResponsePayload["selectedAccount"] | undefined> => {
            const clientAuthTokenData = await getPublicJuspayClientAuthToken({ apiHost, tenant });
            if (shouldShowError) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.SWIGGY_UPI_SDK_ERROR,
                    isSdkAvailable: String(isSdkAvailable),
                    clientAuthToken: clientAuthTokenData?.clientAuthToken || "",
                    extendedCustomerId: clientAuthTokenData?.extendedCustomerId || "",
                    merchantId: merchantId || "",
                    tenant: tenant || "",
                });

                // showing a error toast if we are not able to make the request due to SDK issue or backend(clientAuthTokenData)issue
                setCheckoutState({
                    type: CheckoutActionTypes.GENERIC_PAYMENT_TOAST,
                    payload: {
                        message: INAPP_UPI_ERROR_TITLE,
                        type: TOAST_TYPE.ERROR,
                    },
                });
                return;
            }
            try {
                if (clientAuthTokenData != null) {
                    const juspaySDKResponse = await processInAppUpiBridgeRequest(
                        {
                            merchantId,
                            tenant,
                            ...clientAuthTokenData,
                        },
                        requestState,
                    );
                    const userSelectedAccount = juspaySDKResponse?.selectedAccount;
                    const showSuccessToast = juspaySDKResponse?.showSuccessToast ?? false;
                    if (isMountedRef.current == null) {
                        return;
                    }

                    if (isEmpty(userSelectedAccount)) {
                        if (showSuccessToast) {
                            // showing a info toast if we get success response from SDK and juspaySDKResponse (selectedAccount) is empty/undefined since user have completed the onboarding
                            setCheckoutState({
                                type: CheckoutActionTypes.GENERIC_PAYMENT_TOAST,
                                payload: {
                                    message: INAPP_UPI_SUCCESS_TITLE,
                                    type: TOAST_TYPE.INFO,
                                },
                            });
                        }
                        // we will just refresh the payment page so user gets the updated bank list since onboarding is completed.
                        reloadPayments();
                        return;
                    }

                    return Promise.resolve(userSelectedAccount);
                }
            } catch (error) {
                if (isMountedRef.current == null) {
                    return;
                }

                return Promise.reject();
            }
        },
        [apiHost, isMountedRef, isSdkAvailable, merchantId, reloadPayments, setCheckoutState, shouldShowError, tenant],
    );

    return {
        makeInAppUpiRequest,
        isSdkAvailableForInAppUpi: isSdkAvailable,
    };
};
